/**
 * One label for a bot login wherever the panel offers one (CAL-6224): the
 * username with what tells the vendor's logins apart, and the connection's id -
 * the same number on every screen, unlike a position in a list. Built from the
 * named fields only: the connection list also carries the password for the
 * edit dialog.
 */
import type { AdminVendorConnection } from "types/interfaces/adminUsers";

export type LabelledConnection = Pick<
  AdminVendorConnection,
  "id" | "username" | "client_code" | "host" | "created_at"
>;

export interface ConnectionLabelContext {
  /** False for a login from outside the major job's pool. */
  linked?: boolean;
  /** How many of the open job's tasks run on it. */
  taskCount?: number;
}

export interface ConnectionLabel {
  /** "@john", "CC 12ABC", "acme.myisolved.com" - an open list's first line. */
  primary: string;
  /** "#57", "added 10/21/2026", "3 tasks" - its muted second line. */
  secondary: string;
  /** "#57", "@john", then the rest - a closed select, cut with "...": the
   *  id first, so two logins of one username differ even in a narrow one. */
  short: string;
  /** Both lines on one, for a hover title. */
  full: string;
}

// A middle dot, as the panel separates parts of a label elsewhere.
const SEPARATOR = " \u00b7 ";

/** "https://acme.myisolved.com/" -> "acme.myisolved.com"; empty stays empty. */
export function hostWithoutScheme(host: string | null | undefined): string {
  return (host ?? "")
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .replace(/\/+$/, "");
}

function addedOn(createdAt: string | null | undefined): string {
  if (!createdAt) return "";
  const when = new Date(createdAt);
  return Number.isNaN(when.getTime())
    ? ""
    : `added ${when.toLocaleDateString()}`;
}

export function connectionLabel(
  connection: LabelledConnection | null | undefined,
  id: number,
  context: ConnectionLabelContext = {},
): ConnectionLabel {
  const ref = `#${connection?.id ?? id}`;
  if (!connection) {
    return { primary: ref, secondary: "", short: ref, full: ref };
  }
  const username = (connection.username ?? "").trim();
  const login = username ? `@${username}` : "(no username)";
  const clientCode = (connection.client_code ?? "").trim();
  const tellsApart = [
    ...(clientCode ? [`CC ${clientCode}`] : []),
    hostWithoutScheme(connection.host) || "default host",
  ];
  const { linked, taskCount } = context;
  const details = [
    ref,
    addedOn(connection.created_at),
    linked === false ? "not linked to this major job" : "",
    taskCount != null
      ? `${taskCount} ${taskCount === 1 ? "task" : "tasks"}`
      : "",
  ].filter(Boolean);
  const primary = [login, ...tellsApart].join(SEPARATOR);
  const secondary = details.join(SEPARATOR);
  return {
    primary,
    secondary,
    short: [ref, login, ...tellsApart].join(SEPARATOR),
    full: `${primary}${SEPARATOR}${secondary}`,
  };
}

/** "#57 \u00b7 @john": the start of a closed select's label, for where only the
 *  task's own fields are at hand (the task page, without the connection list). */
export function connectionHeadline(
  id: number,
  username: string | null | undefined,
): string {
  const name = (username ?? "").trim();
  return [`#${id}`, ...(name ? [`@${name}`] : [])].join(SEPARATOR);
}

/** The label of a login by id, from the connections the panel holds. */
export function connectionLabelById(
  connections: readonly LabelledConnection[],
  id: number,
  context: ConnectionLabelContext = {},
): ConnectionLabel {
  return connectionLabel(
    connections.find((connection) => connection.id === id),
    id,
    context,
  );
}
